import { z } from 'zod';
import { ConditionSchema, conditionFieldIds } from './condition.js';
import { FIELD_PATTERN_KEYS, FieldIdSchema, FORM_LIMITS, FormIdSchema, FormKeySchema, ISO_DATE, LocaleSchema, plainText, SectionIdSchema, } from './primitives.js';
/**
 * The versioned intake form definition (ADR-017, FR-INTAKE-01, DATABASE.md §7.8).
 *
 * One published row of `intake_form_versions.schema` is exactly one `FormDefinition`.
 * Versions are immutable: the builder edits `intake_forms.draft_schema` and publishing
 * copies it into a new version, so a submission pinned to a version always renders with
 * the questions the patient actually answered.
 */
/** Every field type the renderer knows. Adding one is a change to both packages. */
export const FIELD_TYPES = [
    'text',
    'textarea',
    'email',
    'phone',
    'number',
    'date',
    'select',
    'radio',
    'checkbox',
    'checkboxGroup',
    'yesNo',
    'signature',
    'file',
    'heading',
    'paragraph',
    'consent',
];
/** Types that carry no answer - they exist to structure the page. */
export const DISPLAY_FIELD_TYPES = ['heading', 'paragraph'];
const DISPLAY_TYPE_SET = new Set(DISPLAY_FIELD_TYPES);
/** True for `heading` and `paragraph`: rendered, never answered, never validated. */
export function isDisplayFieldType(type) {
    return DISPLAY_TYPE_SET.has(type);
}
export const OptionSchema = z.object({
    /** Stored in the answer. Stable across label edits, so reporting survives rewording. */
    value: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[^\s<>][^<>]*$/, { error: 'Option values cannot contain < or >.' }),
    label: plainText(FORM_LIMITS.maxLabel),
    /** Second line under the label in radio and checkbox lists. */
    description: plainText(FORM_LIMITS.maxHelpText).optional(),
});
/**
 * Constraints beyond `required`. `pattern` is a key from the allow-list in primitives.ts,
 * never a raw expression - see the note there.
 */
export const FieldValidationSchema = z
    .object({
    /** `number` fields. */
    min: z.number().optional(),
    max: z.number().optional(),
    /** Text-ish fields. */
    minLength: z.number().int().min(1).max(FORM_LIMITS.maxTextareaAnswer).optional(),
    maxLength: z.number().int().min(1).max(FORM_LIMITS.maxTextareaAnswer).optional(),
    /** `date` fields, inclusive, `YYYY-MM-DD`. */
    minDate: z.string().regex(ISO_DATE).optional(),
    maxDate: z.string().regex(ISO_DATE).optional(),
    /** `checkboxGroup` and multi-`select`. */
    minSelected: z.number().int().min(1).max(FORM_LIMITS.maxOptions).optional(),
    maxSelected: z.number().int().min(1).max(FORM_LIMITS.maxOptions).optional(),
    pattern: z.enum(FIELD_PATTERN_KEYS).optional(),
    /** Overrides the allow-list's own message, e.g. "Enter your member id from the card." */
    patternMessage: plainText(200).optional(),
})
    .superRefine((validation, ctx) => {
    if (validation.min !== undefined &&
        validation.max !== undefined &&
        validation.min > validation.max) {
        ctx.addIssue({ code: 'custom', path: ['max'], message: 'max must not be below min.' });
    }
    if (validation.minLength !== undefined &&
        validation.maxLength !== undefined &&
        validation.minLength > validation.maxLength) {
        ctx.addIssue({
            code: 'custom',
            path: ['maxLength'],
            message: 'maxLength must not be below minLength.',
        });
    }
    if (validation.minDate !== undefined &&
        validation.maxDate !== undefined &&
        validation.minDate > validation.maxDate) {
        ctx.addIssue({
            code: 'custom',
            path: ['maxDate'],
            message: 'maxDate must not be before minDate.',
        });
    }
    if (validation.minSelected !== undefined &&
        validation.maxSelected !== undefined &&
        validation.minSelected > validation.maxSelected) {
        ctx.addIssue({
            code: 'custom',
            path: ['maxSelected'],
            message: 'maxSelected must not be below minSelected.',
        });
    }
});
/** Present on every field, answerable or not. */
const commonShape = {
    id: FieldIdSchema,
    /** Hides the field - and drops its answer - unless the condition holds. */
    visibleWhen: ConditionSchema.optional(),
    /** Half fields pair up on a wide viewport and stack below 640px. */
    width: z.enum(['full', 'half']).optional(),
    /**
     * Documentation only: marks the answer as protected health information for the data map
     * in SECURITY.md. It changes no behaviour - `sensitive` is what drives encryption.
     */
    phi: z.boolean().optional(),
};
/** Present on every field that produces an answer. */
const inputShape = {
    ...commonShape,
    label: plainText(FORM_LIMITS.maxLabel),
    helpText: plainText(FORM_LIMITS.maxHelpText).optional(),
    placeholder: plainText(FORM_LIMITS.maxPlaceholder).optional(),
    required: z.boolean().optional(),
    /**
     * The answer is written to `intake_submissions.answers_sensitive_enc` instead of the
     * plain `answers` column, and is redacted from logs (FR-INTAKE-04, DATABASE.md §5).
     */
    sensitive: z.boolean().optional(),
    validation: FieldValidationSchema.optional(),
};
const optionsShape = {
    options: z
        .array(OptionSchema)
        .min(1, { error: 'Add at least one option.' })
        .max(FORM_LIMITS.maxOptions),
};
export const FieldSchema = z.discriminatedUnion('type', [
    z.object({ ...inputShape, type: z.literal('text') }),
    z.object({
        ...inputShape,
        type: z.literal('textarea'),
        rows: z.number().int().min(2).max(20).optional(),
    }),
    z.object({ ...inputShape, type: z.literal('email') }),
    z.object({ ...inputShape, type: z.literal('phone') }),
    z.object({ ...inputShape, type: z.literal('number'), unit: plainText(20).optional() }),
    z.object({ ...inputShape, type: z.literal('date') }),
    z.object({
        ...inputShape,
        ...optionsShape,
        type: z.literal('select'),
        multiple: z.boolean().optional(),
    }),
    z.object({ ...inputShape, ...optionsShape, type: z.literal('radio') }),
    z.object({ ...inputShape, type: z.literal('checkbox') }),
    z.object({ ...inputShape, ...optionsShape, type: z.literal('checkboxGroup') }),
    z.object({ ...inputShape, type: z.literal('yesNo') }),
    z.object({
        ...inputShape,
        type: z.literal('signature'),
        /** Shown above the name box, e.g. "I confirm the answers above are accurate." */
        statement: plainText(FORM_LIMITS.maxParagraph),
    }),
    z.object({
        ...inputShape,
        type: z.literal('file'),
        /** MIME allow-list, enforced again by the API on `POST /files/:id/complete` (§28.2). */
        accept: z
            .array(z.string().regex(/^[a-z]+\/[a-z0-9.+-]+$/, { error: 'Use MIME types, e.g. "image/jpeg".' }))
            .min(1)
            .max(20),
        maxSizeMb: z.number().int().min(1).max(FORM_LIMITS.maxFileSizeMb),
        multiple: z.boolean().optional(),
    }),
    z.object({
        ...commonShape,
        type: z.literal('heading'),
        text: plainText(FORM_LIMITS.maxLabel),
        level: z.union([z.literal(3), z.literal(4)]).optional(),
    }),
    z.object({
        ...commonShape,
        type: z.literal('paragraph'),
        text: plainText(FORM_LIMITS.maxParagraph),
    }),
    z.object({
        ...inputShape,
        type: z.literal('consent'),
        /** The wording the patient is agreeing to. Rendered above the checkbox. */
        text: plainText(FORM_LIMITS.maxParagraph),
        /** A consent that may be declined is a `checkbox`, not a `consent`. */
        required: z.literal(true),
    }),
]);
/** The text a summary, a PDF or a staff table should use to name a field. */
export function fieldLabel(field) {
    return 'label' in field ? field.label : field.text;
}
export const SectionSchema = z.object({
    id: SectionIdSchema,
    title: plainText(FORM_LIMITS.maxLabel),
    description: plainText(FORM_LIMITS.maxParagraph).optional(),
    fields: z.array(FieldSchema).min(1).max(FORM_LIMITS.maxFieldsPerSection),
    /** Hides the whole section - and drops every answer inside it. */
    visibleWhen: ConditionSchema.optional(),
});
export const FormSettingsSchema = z.object({
    submitLabel: plainText(60),
    /** Whether the patient may leave and come back - drives the autosave contract (M8.7). */
    allowPartialSave: z.boolean(),
    showProgress: z.boolean(),
});
const FormDefinitionShape = z.object({
    id: FormIdSchema,
    /** `intake_forms.key`, stable across versions. */
    key: FormKeySchema,
    title: plainText(FORM_LIMITS.maxLabel),
    description: plainText(FORM_LIMITS.maxParagraph).optional(),
    /** `intake_form_versions.version`, 1-based and immutable once published. */
    version: z.number().int().min(1).max(100_000),
    locale: LocaleSchema,
    sections: z.array(SectionSchema).min(1).max(FORM_LIMITS.maxSections),
    settings: FormSettingsSchema,
});
/**
 * The whole definition, with the cross-field rules a per-field schema cannot express.
 * Everything here is a rule the builder must also enforce, so a definition that fails is
 * a bug in the builder rather than something a patient can trigger.
 */
export const FormDefinitionSchema = FormDefinitionShape.superRefine((definition, ctx) => {
    const sectionIds = new Set();
    const fieldIds = new Set();
    let fieldCount = 0;
    definition.sections.forEach((section, sectionIndex) => {
        if (sectionIds.has(section.id)) {
            ctx.addIssue({
                code: 'custom',
                path: ['sections', sectionIndex, 'id'],
                message: `Duplicate section id "${section.id}".`,
            });
        }
        sectionIds.add(section.id);
        section.fields.forEach((field, fieldIndex) => {
            fieldCount += 1;
            if (fieldIds.has(field.id)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['sections', sectionIndex, 'fields', fieldIndex, 'id'],
                    message: `Duplicate field id "${field.id}".`,
                });
            }
            fieldIds.add(field.id);
            if ('options' in field) {
                const values = new Set();
                field.options.forEach((option, optionIndex) => {
                    if (values.has(option.value)) {
                        ctx.addIssue({
                            code: 'custom',
                            path: [
                                'sections',
                                sectionIndex,
                                'fields',
                                fieldIndex,
                                'options',
                                optionIndex,
                                'value',
                            ],
                            message: `Duplicate option value "${option.value}".`,
                        });
                    }
                    values.add(option.value);
                });
            }
            if (isDisplayFieldType(field.type))
                return;
            if ('sensitive' in field && field.sensitive === true && field.type === 'file') {
                ctx.addIssue({
                    code: 'custom',
                    path: ['sections', sectionIndex, 'fields', fieldIndex, 'sensitive'],
                    message: 'File answers hold ids only; the object itself is protected by storage, not by this flag.',
                });
            }
        });
    });
    if (fieldCount > FORM_LIMITS.maxFields) {
        ctx.addIssue({
            code: 'custom',
            path: ['sections'],
            message: `A form may hold at most ${FORM_LIMITS.maxFields} fields; this one has ${fieldCount}.`,
        });
    }
    const checkCondition = (condition, path, ownerId) => {
        for (const referenced of conditionFieldIds(condition)) {
            if (!fieldIds.has(referenced)) {
                ctx.addIssue({
                    code: 'custom',
                    path,
                    message: `Condition refers to unknown field "${referenced}".`,
                });
                continue;
            }
            if (referenced === ownerId) {
                ctx.addIssue({ code: 'custom', path, message: 'A field cannot depend on its own answer.' });
            }
        }
    };
    definition.sections.forEach((section, sectionIndex) => {
        if (section.visibleWhen) {
            checkCondition(section.visibleWhen, ['sections', sectionIndex, 'visibleWhen'], null);
        }
        section.fields.forEach((field, fieldIndex) => {
            if (field.visibleWhen) {
                checkCondition(field.visibleWhen, ['sections', sectionIndex, 'fields', fieldIndex, 'visibleWhen'], field.id);
            }
        });
    });
});
/** Every field in document order, sections included. */
export function allFields(definition) {
    return definition.sections.flatMap((section) => section.fields);
}
/** Index of fields by id. O(1) lookups for the renderer and the validator. */
export function fieldIndex(definition) {
    return new Map(allFields(definition).map((field) => [field.id, field]));
}
//# sourceMappingURL=definition.js.map